import { useNavigate } from "react-router-dom";
import request from "@/lib/api/handler";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { posthog } from "posthog-js";

import { QueryKeys } from "@/queries/utils";
import { ChatMessage } from "@/queries/venturi/venturi-public-chat";

type ChatResponse =
  | {
      message: string;
    }
  | {
      jobId: string;
      claimToken: string;
    };

export const useSendVenturiPublicChatMessage = (chatId: string) => {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (message: string) => {
      return request<ChatResponse>({
        method: "POST",
        path: `/api/jobOpening/public/chat/${chatId}`,
        body: JSON.stringify({
          message,
        }),
      });
    },
    onMutate: (variables) => {
      // Fetch any existing data for the job updated
      const prev = qc.getQueryData<ChatMessage[]>(
        QueryKeys.venturiPublicChat(chatId),
      );

      if (prev) {
        // Update the data
        qc.setQueryData<ChatMessage[]>(QueryKeys.venturiPublicChat(chatId), [
          ...prev,
          {
            from: "User",
            message: variables,
          },
        ]);
      }
    },
    onSuccess: (data) => {
      // If a job was created, store the claim token in local storage
      if ("jobId" in data) {
        const existingTokens = JSON.parse(
          localStorage.getItem("jobClaimTokens") ?? "[]",
        ) as string[];
        existingTokens.push(data.claimToken);
        localStorage.setItem("jobClaimTokens", JSON.stringify(existingTokens));

        posthog.capture("hire_in_seconds_job_created");

        // Navigate to the public job opening page
        return navigate(`/hello/opportunity/${data.jobId}`);
      }

      // Fetch any existing data for the job updated
      const prev = qc.getQueryData<ChatMessage[]>(
        QueryKeys.venturiPublicChat(chatId),
      );

      if (prev) {
        // Update the data
        qc.setQueryData<ChatMessage[]>(QueryKeys.venturiPublicChat(chatId), [
          ...prev,
          {
            from: "Bot",
            message: data.message,
          },
        ]);
      }
    },
  });

  return {
    sendVenturiPublicChatMessage: mutate,
    isSendingVenturiPublicChatMessage: isPending,
    sendVenturiPublicChatMessageError: error,
  };
};
